import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { DqGateService } from '../../../services/dq-gate.service';
import { UserService } from '../../../services/user.service';
import { IDQGateQuarantineModel } from '../../../entities-api/IDQGateQuarantineModel';
import { IDQGateDetailModel } from '../../../entities-api/IDQGateDetailModel';
import { IDQGateSnapshotModel } from '../../../entities-api/IDQGateSnapshotModel';

interface QuarantinePanelState {
    expanded: boolean;
    loading: boolean;
    details: IDQGateDetailModel[];
    snapshots: IDQGateSnapshotModel[];
    selectedIds: Set<string>;
}

@Component({
    selector: 'opm-dq-gate-review',
    templateUrl: './opm-dq-gate-review.component.html',
    styleUrls: ['./opm-dq-gate-review.component.less'],
    standalone: false
})
export class OpmDqGateReviewComponent implements OnInit, OnDestroy {

    readWriteAccess = false;

    pendingRecords: IDQGateQuarantineModel[] = [];
    isLoadingPending = false;

    /** Per-quarantine expand / selection state keyed by QuarantineId */
    panelState: { [quarantineId: string]: QuarantinePanelState } = {};

    isConfirmModalVisible = false;
    isApproveModalVisible = false;
    isRetryModalVisible = false;
    pendingActionQuarantineId: string | null = null;
    pendingActionSnapshotIds: string[] = [];
    pendingRetryQuarantineId: string | null = null;
    rejectComment = '';
    isSubmitting = false;

    @ViewChild('rejectTextarea') rejectTextareaEl?: ElementRef<HTMLTextAreaElement>;

    constructor(
        private dqGateService: DqGateService,
        private userService: UserService,
        private toastr: ToastrService
    ) { }

    ngOnInit(): void {
        document.documentElement.classList.add('dq-gate-page');
        document.body.classList.add('dq-gate-page');

        this.userService.getDQGateAccessData().then(v => this.readWriteAccess = v);
        this.loadPending();
    }

    ngOnDestroy(): void {
        document.documentElement.classList.remove('dq-gate-page');
        document.body.classList.remove('dq-gate-page');
    }

    get violationBatchCount(): number {
        return this.pendingRecords.length;
    }

    loadPending(): void {
        this.isLoadingPending = true;
        this.dqGateService.getPending()
            .then(records => {
                this.pendingRecords = records;
                this.prunePanelState();
                this.isLoadingPending = false;
            })
            .catch(() => {
                this.toastr.error('Failed to load quarantine list.');
                this.isLoadingPending = false;
            });
    }

    isExpanded(quarantineId: string): boolean {
        return !!this.panelState[quarantineId]?.expanded;
    }

    toggleQuarantine(record: IDQGateQuarantineModel): void {
        const id = record.QuarantineId;
        const state = this.ensurePanel(id);

        if (state.expanded) {
            state.expanded = false;
            return;
        }

        state.expanded = true;
        if (state.details.length === 0 && state.snapshots.length === 0 && !state.loading) {
            this.loadPanelData(id);
        }
    }

    loadPanelData(quarantineId: string): void {
        const state = this.ensurePanel(quarantineId);
        state.loading = true;

        Promise.all([
            this.dqGateService.getDetail(quarantineId),
            this.dqGateService.getSnapshot(quarantineId)
        ])
            .then(([details, snapshots]) => {
                state.details = details;
                state.snapshots = snapshots;
                state.selectedIds = new Set<string>();
                state.loading = false;
            })
            .catch(() => {
                this.toastr.error('Failed to load quarantine details.');
                state.loading = false;
            });
    }

    getSelectedCount(quarantineId: string): number {
        return this.panelState[quarantineId]?.selectedIds.size ?? 0;
    }

    getThresholdViolations(details: any[]): any[] {
        return (details || []).filter(d => !d.OldValue && !d.NewValue);
    }

    getFieldChanges(details: any[]): any[] {
        return (details || []).filter(d => d.OldValue != null || d.NewValue != null);
    }

    isSnapshotSelected(quarantineId: string, snapshotId: string): boolean {
        return !!this.panelState[quarantineId]?.selectedIds.has(snapshotId);
    }

    isAllSelected(quarantineId: string): boolean {
        const state = this.panelState[quarantineId];
        if (!state || state.snapshots.length === 0) {
            return false;
        }
        return state.selectedIds.size === state.snapshots.length;
    }

    isPartialSelected(quarantineId: string): boolean {
        const state = this.panelState[quarantineId];
        if (!state || state.snapshots.length === 0) {
            return false;
        }
        return state.selectedIds.size > 0 && state.selectedIds.size < state.snapshots.length;
    }

    toggleSnapshot(quarantineId: string, snapshotId: string, checked: boolean): void {
        const state = this.ensurePanel(quarantineId);
        if (checked) {
            state.selectedIds.add(snapshotId);
        } else {
            state.selectedIds.delete(snapshotId);
        }
    }

    toggleSelectAll(quarantineId: string, checked: boolean): void {
        const state = this.ensurePanel(quarantineId);
        if (checked) {
            state.selectedIds = new Set(state.snapshots.map(s => s.SnapshotId));
        } else {
            state.selectedIds.clear();
        }
    }

    canAct(quarantineId: string): boolean {
        return this.readWriteAccess && this.getSelectedCount(quarantineId) > 0 && !this.isSubmitting;
    }

    promptApprove(quarantineId: string): void {
        if (!this.canAct(quarantineId)) {
            return;
        }
        const state = this.panelState[quarantineId];
        this.pendingActionQuarantineId = quarantineId;
        this.pendingActionSnapshotIds = Array.from(state.selectedIds);
        this.isApproveModalVisible = true;
    }

    confirmApprove(): void {
        if (!this.pendingActionQuarantineId) {
            return;
        }

        const quarantineId = this.pendingActionQuarantineId;
        const snapshotIds  = [...this.pendingActionSnapshotIds];
        this.isApproveModalVisible = false;
        this.pendingActionQuarantineId = null;
        this.isSubmitting = true;

        this.dqGateService.approve(quarantineId, snapshotIds)
            .then((result: any) => {
                if (result?.QuarantineClosed) {
                    this.pendingRecords = this.pendingRecords.filter(r => r.QuarantineId !== quarantineId);
                    delete this.panelState[quarantineId];
                    this.toastr.success('All pending records approved. They will be applied by the hourly process.');
                } else {
                    this.toastr.success(
                        `Approved ${result?.AppliedCount ?? snapshotIds.length} record(s). ${result?.RemainingCount ?? 0} pending remaining.`);
                    this.loadPanelData(quarantineId);
                    const header = this.pendingRecords.find(r => r.QuarantineId === quarantineId);
                    if (header) {
                        header.SnapshotCount = result?.RemainingCount ?? 0;
                        header.Status = result?.Status || 'PartiallyReviewed';
                    }
                }
            })
            .catch((err: any) => {
                const status = err?.status;
                const message = err?.error?.Message || err?.error?.message || err?.message;
                if (status === 409) {
                    this.toastr.error(
                        message || 'This quarantine was already approved or rejected.');
                    this.loadPending();
                } else {
                    this.toastr.error(message || 'Failed to approve selected records.');
                }
            })
            .then(() => {
                this.isSubmitting = false;
            });
    }

    promptReject(quarantineId: string): void {
        if (!this.canAct(quarantineId)) {
            return;
        }
        const state = this.panelState[quarantineId];
        this.pendingActionQuarantineId = quarantineId;
        this.pendingActionSnapshotIds = Array.from(state.selectedIds);
        this.rejectComment = '';
        this.rejectCommentTouched = false;
        this.isConfirmModalVisible = true;
    }

    canRetry(quarantineId: string): boolean {
        const record = this.pendingRecords.find(r => r.QuarantineId === quarantineId);
        return this.readWriteAccess && !!record && record.Status === 'Failed' && record.RetryCount < 3 && !this.isSubmitting;
    }

    promptRetry(quarantineId: string): void {
        if (!this.canRetry(quarantineId)) {
            return;
        }
        this.pendingRetryQuarantineId = quarantineId;
        this.isRetryModalVisible = true;
    }

    confirmRetry(): void {
        if (!this.pendingRetryQuarantineId) {
            return;
        }
        const quarantineId = this.pendingRetryQuarantineId;
        this.isRetryModalVisible = false;
        this.pendingRetryQuarantineId = null;
        this.isSubmitting = true;

        this.dqGateService.retry(quarantineId)
            .then(() => {
                this.toastr.success('Quarantine reset for retry. It will be reprocessed on the next sync run.');
                this.loadPending();
            })
            .catch((err: any) => {
                const message = err?.error?.Message || err?.error?.message || err?.message;
                this.toastr.error(message || 'Failed to reset quarantine for retry.');
            })
            .then(() => {
                this.isSubmitting = false;
            });
    }

    cancelAction(): void {
        this.isConfirmModalVisible = false;
        this.isApproveModalVisible = false;
        this.isRetryModalVisible = false;
        this.pendingActionQuarantineId = null;
        this.pendingActionSnapshotIds = [];
        this.pendingRetryQuarantineId = null;
        this.rejectComment = '';
        this.rejectCommentTouched = false;
    }

    rejectCommentTouched = false;

    get isRejectCommentValid(): boolean {
        return (this.rejectComment?.trim()?.length ?? 0) >= 10;
    }

    onRejectCommentBlur(): void {
        this.rejectCommentTouched = true;
    }

    confirmReject(): void {
        this.rejectCommentTouched = true;

        if (!this.isRejectCommentValid) {
            this.toastr.warning('Please enter a rejection reason of at least 10 characters.');
            return;
        }

        if (!this.pendingActionQuarantineId || this.pendingActionSnapshotIds.length === 0) {
            return;
        }

        const quarantineId = this.pendingActionQuarantineId;
        const snapshotIds  = [...this.pendingActionSnapshotIds];
        // Read directly from the DOM element — immune to any ngModel timing issue
        const comment = (this.rejectTextareaEl?.nativeElement?.value ?? this.rejectComment ?? '').trim();
        this.cancelAction();
        this.isSubmitting = true;

        this.dqGateService.reject(quarantineId, snapshotIds, comment)
            .then(result => {
                if (result.QuarantineClosed) {
                    this.pendingRecords = this.pendingRecords.filter(r => r.QuarantineId !== quarantineId);
                    delete this.panelState[quarantineId];
                    this.toastr.success('Quarantine rejected and removed from the queue.');
                } else {
                    this.toastr.success(
                        `Rejected ${result.DeletedCount} record(s). ${result.RemainingCount} remaining.`);
                    this.loadPanelData(quarantineId);
                    const header = this.pendingRecords.find(r => r.QuarantineId === quarantineId);
                    if (header) {
                        header.SnapshotCount = result.RemainingCount;
                        header.Status = result.Status || 'PartiallyReviewed';
                    }
                }
            })
            .catch((err: any) => {
                const status = err?.status;
                const message = err?.error?.Message || err?.error?.message || err?.message;
                if (status === 409) {
                    this.toastr.error(
                        message || 'This quarantine was already approved or rejected.');
                    this.loadPending();
                } else {
                    this.toastr.error(message || 'Failed to reject selected records.');
                }
            })
            .then(() => {
                this.isSubmitting = false;
            });
    }

    private ensurePanel(quarantineId: string): QuarantinePanelState {
        if (!this.panelState[quarantineId]) {
            this.panelState[quarantineId] = {
                expanded: false,
                loading: false,
                details: [],
                snapshots: [],
                selectedIds: new Set<string>()
            };
        }
        return this.panelState[quarantineId];
    }

    private prunePanelState(): void {
        const liveIds = new Set(this.pendingRecords.map(r => r.QuarantineId));
        Object.keys(this.panelState).forEach(id => {
            if (!liveIds.has(id)) {
                delete this.panelState[id];
            }
        });
    }
}
